import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import { useFormikContext, setIn, getIn } from 'formik';
import { groupBy, merge } from 'lodash';
import uuidv4 from 'uuid/v4';

import { useHelmChartValues } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartValues';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Input } from '@@/form-components/Input';
import { Alert } from '@@/Alert';
import { InlineLoader } from '@@/InlineLoader';

import { useAddon } from '../queries/useAddon';
import { PORTAINER_RUN_ADDON_ID } from '../constants';

import { ConnectionFieldInput } from './ConnectionFieldInput';
import {
  parseConnectionFields,
  formatKeyLabel,
  ConnectionField,
} from './parseConnectionFields';
import { InstallFormValues } from './types';

// Temporary front-end auto-fill for the portainer-run add-on's required
// encryption key; see withGeneratedEncryptionKey below.
const ENCRYPTION_KEY_FIELD = 'secret.ENCRYPTION_KEY';

export function ConfigurationStep({
  readOnly = false,
  valuesOverride,
  valuesOverrideReady = true,
}: {
  readOnly?: boolean;
  // When provided, the form is seeded with these values merged over the chart
  // defaults (the detail view passes the installed release's actual values).
  valuesOverride?: Record<string, unknown>;
  // False while the release's values are still loading; the form waits so it
  // seeds defaults + overrides together. Always true on install (no release).
  valuesOverrideReady?: boolean;
}) {
  const { params } = useCurrentStateAndParams();
  const addonId = String(params.id ?? '');
  const {
    values: { version },
    setFieldValue,
  } = useFormikContext<InstallFormValues>();

  // The detail record carries the catalog chart source and the published
  // versions (latest-first), so the wizard needs no dedicated endpoints.
  const addonQuery = useAddon(addonId);
  const record = addonQuery.data?.record;
  const chartSource = record?.helmChartRepository ?? '';
  const versions = record?.availableVersions ?? [];

  // On a fresh install the version field starts empty - default it to the latest
  // (the head of the list) once the versions load.
  const latestVersion = versions[0];
  useEffect(() => {
    if (version || !latestVersion) {
      return;
    }
    setFieldValue('version', latestVersion);
  }, [version, latestVersion, setFieldValue]);

  // The chart source is a self-contained oci:// reference, so the generic Helm
  // values endpoint resolves it without a repo. Passing an empty chart until a
  // version is chosen keeps the query disabled, so selecting a version never
  // fires an unnecessary call.
  const valuesQuery = useHelmChartValues({
    chart: version ? chartSource : '',
    version,
  });
  const valuesYaml = valuesQuery.data?.values;

  const fields = useMemo(
    () => (valuesYaml ? parseConnectionFields(valuesYaml) : []),
    [valuesYaml]
  );

  // Seed once both the chart defaults and the release override are ready, so the
  // single seed already has the override in hand. Re-seeds only on version change.
  const seededVersionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!valuesQuery.isSuccess || !valuesOverrideReady) {
      return;
    }
    if (seededVersionRef.current === version) {
      return;
    }
    seededVersionRef.current = version;

    const defaults = fields.reduce<Record<string, unknown>>(
      (acc, field) => setIn(acc, field.key, field.value),
      {}
    );
    // When seeding from an installed release (valuesOverride), keep the selected
    // version's defaults for any key the release doesn't carry — a field that's
    // new in this version would otherwise render empty and get submitted as a
    // blank override. The release's values win for the keys it does set.
    const seed = valuesOverride
      ? merge({}, defaults, valuesOverride)
      : withGeneratedEncryptionKey(addonId, defaults);
    setFieldValue('values', seed);
  }, [
    valuesQuery.isSuccess,
    valuesOverrideReady,
    version,
    fields,
    valuesOverride,
    setFieldValue,
    addonId,
  ]);

  if (addonQuery.isError || record?.versionCheckError) {
    return (
      <Alert color="error">
        Unable to load the add-on&apos;s chart versions.
      </Alert>
    );
  }

  return (
    <FormSection title="Set up connection">
      <FormControl
        label="Chart source"
        size="vertical"
        inputId="addon-chart-source"
        isLoading={addonQuery.isLoading}
        loadingText="Loading chart source..."
      >
        <Input
          id="addon-chart-source"
          value={chartSource}
          disabled
          readOnly
          data-cy="addon-install-chart-source"
        />
      </FormControl>

      <FormControl
        label="Version"
        size="vertical"
        inputId="addon-install-version-select"
      >
        <PortainerSelect<string>
          inputId="addon-install-version-select"
          data-cy="addon-install-version-select"
          value={version}
          options={versions.map((v, i) => ({
            label: i === 0 ? `${v} (latest)` : v,
            value: v,
          }))}
          onChange={(v) => setFieldValue('version', v ?? '')}
          placeholder={
            versions.length ? 'Select a version...' : 'No versions found'
          }
          disabled={readOnly || !versions.length}
        />
      </FormControl>

      {version && (
        <FormSection title="Add-on configuration" titleSize="sm">
          {valuesQuery.isError ? (
            <Alert color="error">
              Unable to load the add-on&apos;s default configuration.
            </Alert>
          ) : valuesQuery.isSuccess && valuesOverrideReady ? (
            <ConnectionFields fields={fields} readOnly={readOnly} />
          ) : (
            <InlineLoader>Loading configuration...</InlineLoader>
          )}
        </FormSection>
      )}
    </FormSection>
  );
}

function ConnectionFields({
  fields,
  readOnly,
}: {
  fields: ConnectionField[];
  readOnly?: boolean;
}) {
  if (fields.length === 0) {
    return (
      <p className="text-sm text-[var(--text-muted-color)]">
        This add-on has no configurable default values.
      </p>
    );
  }

  const groups = groupFieldsByParent(fields);

  return (
    <>
      {groups.map((group) => (
        <Fragment key={group.parent || '__root__'}>
          {/* Nested values (e.g. image.tag) are grouped under a header named
              after their parent (Image) to divide the form into sections. */}
          {group.parent && (
            <FormSectionTitle titleSize="sm">
              {formatKeyLabel(lastSegment(group.parent))}
            </FormSectionTitle>
          )}
          {group.fields.map((field) => (
            <ConnectionFieldInput
              key={field.key}
              field={field}
              readOnly={readOnly}
            />
          ))}
        </Fragment>
      ))}
    </>
  );
}

interface FieldGroup {
  parent: string;
  fields: ConnectionField[];
}

// Groups fields by their parent path. Sections that contain a required field
// float to the top so required inputs aren't buried; among the rest, top-level
// (ungrouped) fields lead, then each named section in the chart's order. Within
// a section, required fields come first.
function groupFieldsByParent(fields: ConnectionField[]): FieldGroup[] {
  const grouped = groupBy(fields, (field) => parentKey(field.key));

  const groups = Object.entries(grouped).map(([parent, sectionFields]) => ({
    parent,
    fields: sectionFields.sort(
      (a, b) => Number(b.required) - Number(a.required)
    ),
  }));

  // Stable sort keeps the chart's order within each rank.
  return groups.sort((a, b) => groupRank(a) - groupRank(b));
}

// Ordering rank for a section: required-containing sections first, then the
// top-level (headerless) section, then the remaining named sections.
function groupRank(group: FieldGroup): number {
  if (group.fields.some((field) => field.required)) {
    return 0;
  }
  return group.parent === '' ? 1 : 2;
}

// Temporary: the portainer-run chart ships an empty, required encryptionKey the
// user must fill in by hand. Until a chart/server-side default exists, pre-fill
// it with a generated key on a fresh install so the form works out of the box.
// Only fires when the chart default is empty; an installed release's own key is
// preserved via valuesOverride (this branch runs only for a fresh install).
function withGeneratedEncryptionKey(
  addonId: string,
  defaults: Record<string, unknown>
): Record<string, unknown> {
  if (
    addonId !== PORTAINER_RUN_ADDON_ID ||
    getIn(defaults, ENCRYPTION_KEY_FIELD) !== ''
  ) {
    return defaults;
  }
  return setIn(defaults, ENCRYPTION_KEY_FIELD, generateEncryptionKey());
}

// Two UUIDs with hyphens stripped give 64 hex chars, matching the chart's
// suggested `openssl rand -hex 32` and clearing its 32-char minimum. The uuid
// wrapper avoids crypto.randomUUID, which Portainer's non-secure (HTTP) context
// forbids.
function generateEncryptionKey(): string {
  return `${uuidv4()}${uuidv4()}`.replace(/-/g, '');
}

function parentKey(key: string): string {
  return key.split('.').slice(0, -1).join('.');
}

function lastSegment(key: string): string {
  return key.split('.').at(-1) ?? key;
}
